import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiStar, FiZap, FiLock } = FiIcons;

const TierCard = ({ tier, isSubscribed, onSubscribe, userId, isActive }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!userId || isSubscribed || loading) return;
    setLoading(true);
    await onSubscribe({ tierId: tier.id, amount: tier.price });
    setLoading(false);
  };

  return (
    <div className={`relative rounded-3xl border p-8 space-y-6 transition-all ${
      isSubscribed
        ? 'border-green-500/50 bg-green-500/5'
        : isActive
        ? 'border-white bg-white/5 scale-[1.02]'
        : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-600'
    }`}>
      {isActive && !isSubscribed && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-white text-black text-xs font-black uppercase tracking-widest rounded-full">
          Most Popular
        </div>
      )}
      {isSubscribed && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-green-500 text-black text-xs font-black uppercase tracking-widest rounded-full">
          Active
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color || '#3b82f6' }} />
          <h3 className="text-lg font-black uppercase tracking-tight text-white">{tier.name}</h3>
        </div>
        <div className="flex items-end gap-1">
          <span className="text-4xl font-black text-white">${tier.price}</span>
          <span className="text-zinc-500 font-bold text-sm pb-1">/month</span>
        </div>
        {tier.description && (
          <p className="text-zinc-400 text-sm leading-relaxed">{tier.description}</p>
        )}
      </div>

      {tier.perks?.length > 0 && (
        <ul className="space-y-3">
          {tier.perks.map((perk, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <SafeIcon icon={FiCheck} className="text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-zinc-300 font-bold">{perk}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={handleClick}
        disabled={isSubscribed || loading || !userId}
        className={`w-full py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
          isSubscribed
            ? 'bg-green-500/20 text-green-400 border border-green-500/30 cursor-default'
            : !userId
            ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
            : 'bg-white text-black hover:scale-[1.02] active:scale-95 shadow-xl'
        }`}>
        {loading ? 'Processing...' : isSubscribed ? '✓ Subscribed' : !userId ? 'Sign In to Subscribe' : `Subscribe · $${tier.price}/mo`}
      </button>
    </div>
  );
};

const SubscriptionTiers = ({ tiers, artistId, userId, hasSubscription, getSubscriptionTier, onSubscribe }) => {
  if (!tiers?.length) return null;

  const activeTierId = getSubscriptionTier?.(artistId);
  const midIndex = Math.floor(tiers.length / 2);

  return (
    <section className="space-y-8">
      <div className="space-y-2">
        <span className="text-xs font-black tracking-widest text-blue-500 uppercase">Support Tiers</span>
        <h2 className="text-4xl font-black tracking-tighter text-white uppercase">Become a Patron</h2>
        <p className="text-zinc-400 font-bold">Choose how you want to support this artist monthly.</p>
      </div>
      <div className={`grid gap-6 ${tiers.length === 1 ? 'grid-cols-1 max-w-sm' : tiers.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'}`}>
        {tiers.map((tier, i) => (
          <TierCard
            key={tier.id}
            tier={tier}
            isSubscribed={activeTierId === tier.id}
            isActive={i === midIndex && tiers.length > 1}
            onSubscribe={onSubscribe}
            userId={userId}
          />
        ))}
      </div>
    </section>
  );
};

export default SubscriptionTiers;